/** @domain document -- File uploads, rich text content, document metadata, and document template references. */
import { z } from 'zod';
import { createDomainField } from './types';
import { trimExtraSpaces } from './formatters';

const define = createDomainField('document');

// ---------------------------------------------------------------------------
// File upload fields
// ---------------------------------------------------------------------------

// documentFile (any file) and pdfFile (PDF only) share the label 'Document' — variants for different accepted file types
export const documentFile = define({
  label: 'Document',
  hint: 'PDF, JPG, PNG, DOCX (max 10MB)',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const pdfFile = define({
  label: 'Document',
  hint: 'PDF only (max 10MB)',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const imageFile = define({
  label: 'Image',
  hint: 'PNG or JPG files only',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const logoFile = define({
  label: 'Logo',
  hint: 'PNG, JPG, or JPEG (max 2MB)',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const faviconFile = define({
  label: 'Favicon',
  hint: 'ICO, PNG, or JPG (max 512KB)',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const excelFile = define({
  label: 'Excel File',
  hint: 'XLSX or XLS files only',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const zipFile = define({
  label: 'ZIP File',
  hint: 'ZIP archive only',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const jsonFile = define({
  label: 'JSON File',
  hint: 'JSON files only',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const approvalDocumentFile = define({
  label: 'Approval Document',
  hint: 'PDF, JPG, PNG, DOCX, MSG (max 10MB)',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

export const awardAcceptanceDocumentFile = define({
  label: 'Acceptance Document',
  hint: 'PDF, JPG, PNG, DOCX (max 10MB)',
  schema: z.any(),
  adapterHint: 'file',
  displayFormat: 'file',
});

// ---------------------------------------------------------------------------
// Rich text content fields
// ---------------------------------------------------------------------------

export const documentContent = define({
  label: 'Document Content',
  placeholder: 'Enter content',
  schema: z.string(),
  adapterHint: 'richtext',
  displayFormat: 'html',
});

export const termsAndConditionsContent = define({
  label: 'Terms & Conditions',
  placeholder: 'Enter terms and conditions',
  schema: z.string(),
  adapterHint: 'richtext',
  displayFormat: 'html',
});

export const agreementTemplateContent = define({
  label: 'Agreement Content',
  placeholder: 'Enter agreement content',
  schema: z.string(),
  adapterHint: 'richtext',
  displayFormat: 'html',
});

// ---------------------------------------------------------------------------
// Document metadata fields
// ---------------------------------------------------------------------------

export const documentName = define({
  label: 'Document Name',
  placeholder: 'Enter document name',
  schema: z
    .string()
    .max(200, 'Must be at most 200 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const documentType = define({
  label: 'Document Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const documentStatus = define({
  label: 'Status',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const templateName = define({
  label: 'Template Name',
  placeholder: 'Enter template name',
  schema: z
    .string()
    .max(200, 'Must be at most 200 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const portalPageId = define({
  label: 'Portal Page',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const isDocumentActive = define({
  label: 'Active',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Grant/award document reference fields (FK selects)
// ---------------------------------------------------------------------------

export const grantAcceptanceTemplateId = define({
  label: 'Grant Acceptance Template',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const grantAgreementDocumentName = define({
  label: 'Grant Agreement Document',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const grantNoticeDocumentName = define({
  label: 'Grant Notice Document',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const termsAndConditionDocumentName = define({
  label: 'Terms & Condition Document',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});
